// 🎓 Plugin de Tutorial Independiente para ExportAI
// Este plugin se puede instalar globalmente sin modificar ningún componente existente

import TutorialSystem from "./tutorials/TutorialSystem.vue";

const TutorialPlugin = {
  install(Vue, options = {}) {
    // Registrar el componente globalmente
    Vue.component("TutorialSystem", TutorialSystem);

    // Obtener la instancia de i18n del root
    let i18nInstance = null;

    // Función para obtener la instancia de i18n
    const getI18nInstance = (vm) => {
      if (i18nInstance) return i18nInstance;
      if (vm.$i18n) return vm.$i18n;
      if (vm.$root && vm.$root.$i18n) return vm.$root.$i18n;
      return null;
    };

    // Método para inicializar i18n cuando esté disponible
    Vue.prototype.$initTutorialI18n = function (i18n) {
      if (i18n) {
        i18nInstance = i18n;
      }
    };

    // Agregar método global para mostrar tutorial
    Vue.prototype.$showTutorial = function (modelName = "") {
      try {
        // Obtener la instancia de i18n
        const i18n = getI18nInstance(this);
        if (!i18n) {
          console.error("❌ No se pudo obtener la instancia de i18n");
          return null;
        }

        // Crear una instancia del tutorial dinámicamente
        const TutorialConstructor = Vue.extend(TutorialSystem);

        const tutorialInstance = new TutorialConstructor({
          propsData: {
            modelName: modelName,
            buttonPosition: "bottom-right",
            showButtonText: true,
            autoShowTooltip: true,
            autoStart: false,
            hideInternalButton: true,
          },
          // Pasar el contexto de i18n
          i18n: i18n,
        });

        // Montar el tutorial
        tutorialInstance.$mount();

        document.body.appendChild(tutorialInstance.$el);

        // Activar el tutorial para que se muestre el modal
        setTimeout(() => {
          tutorialInstance.activateTutorial();
        }, 100);

        // Retornar la instancia para control externo
        return tutorialInstance;
      } catch (error) {
        console.error("❌ Error al crear tutorial:", error);
        return null;
      }
    };


    // Agregar método global para mostrar tutorial automáticamente
    Vue.prototype.$showTutorialAuto = function (modelName = "") {
      try {
        // Obtener la instancia de i18n
        const i18n = getI18nInstance(this);
        if (!i18n) {
          console.error("❌ No se pudo obtener la instancia de i18n");
          return null;
        }

        const TutorialConstructor = Vue.extend(TutorialSystem);
        const tutorialInstance = new TutorialConstructor({
          propsData: {
            modelName: modelName,
            buttonPosition: "bottom-right",
            showButtonText: true,
            autoShowTooltip: true,
            autoStart: true, // Iniciar automáticamente
          },
          // Pasar el contexto de i18n
          i18n: i18n,
        });

        tutorialInstance.$mount();
        document.body.appendChild(tutorialInstance.$el);

        return tutorialInstance;
      } catch (error) {
        console.error("❌ Error al crear tutorial automático:", error);
        return null;
      }
    };

    // Método inteligente para detectar y mostrar el tutorial apropiado
    Vue.prototype.$showContextualTutorial = function () {
      try {
        if (!this.$route) {
          return null;
        }

        const currentPath = this.$route.path;

        // Tutorial para dynamic-model (listados) - SOLO URLs con una barra: /dynamic-model/nombre
        if (currentPath.match(/^\/dynamic-model\/[^\/]+$/)) {
          // Extraer nombre del modelo
          let modelName = "Modelo";
          if (this.$route.params.modelName) {
            modelName = this.$route.params.modelName;
          } else {
            const pathParts = currentPath.split("/");
            if (pathParts.length > 2) {
              modelName = pathParts[2];
            }
          }

          return this.$showTutorial(modelName);
        }

        // Página no reconocida
        else {
          return null;
        }
      } catch (error) {
        console.error("❌ Error al detectar tutorial contextual:", error);
        return null;
      }
    };

    // Método global para mostrar tutorial del calendario
    Vue.prototype.$showCalendarTutorial = function () {
      try {
        // Obtener la instancia de i18n
        const i18n = getI18nInstance(this);
        if (!i18n) {
          console.error("❌ No se pudo obtener la instancia de i18n");
          return null;
        }

        // Crear instancia del tutorial del calendario
        const TutorialConstructor = Vue.extend(TutorialSystem);
        const tutorialInstance = new TutorialConstructor({
          propsData: {
            tutorialType: "calendar",
            buttonPosition: "bottom-right",
            showButtonText: true,
            autoShowTooltip: true,
            autoStart: true,
          },
          // Pasar el contexto de i18n
          i18n: i18n,
        });

        // Montar el tutorial
        tutorialInstance.$mount();

        document.body.appendChild(tutorialInstance.$el);

        // Activar el tutorial para que se muestre el modal
        setTimeout(() => {
          tutorialInstance.activateTutorial();
        }, 100);

        // Retornar la instancia para control externo
        return tutorialInstance;
      } catch (error) {
        console.error("❌ Error al crear tutorial del calendario:", error);
        return null;
      }
    };

    // Agregar método global para resetear tutorial
    Vue.prototype.$resetTutorial = function () {
      localStorage.removeItem("gotrading-tutorial-completed");
      localStorage.removeItem("gotrading-tutorial-dismissed");

      // Limpiar estado del tutorial activo
      if (this.$root) {
        this.$root.$tutorialActive = false;
      }
    };

    // Método global para limpiar tutoriales
    Vue.prototype.$clearTutorial = function () {
      // Resetear estado global
      if (this.$root) {
        this.$root.$tutorialActive = false;
      }

      // Remover solo el overlay del tutorial, NO el sistema completo
      const tutorialOverlays = document.querySelectorAll(
        ".tutorial-overlay, .tutorial-container"
      );

      tutorialOverlays.forEach((element) => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        } else {
          element.remove();
        }
      });

    };

    // Agregar método global para verificar estado del tutorial
    Vue.prototype.$getTutorialStatus = function () {
      return {
        completed:
          localStorage.getItem("gotrading-tutorial-completed") === "true",
        dismissed:
          localStorage.getItem("gotrading-tutorial-dismissed") === "true",
      };
    };

    // Agregar directiva personalizada para activar tutorial en elementos
    Vue.directive("tutorial", {
      bind(el, binding, vnode) {
        const modelName = binding.value || "";

        // Agregar botón de tutorial al elemento
        const tutorialButton = document.createElement("button");
        tutorialButton.className =
          "btn btn-sm btn-outline-info tutorial-trigger";
        tutorialButton.innerHTML =
          '<i class="fas fa-question-circle"></i> Ayuda';
        tutorialButton.title = "Iniciar tutorial interactivo";

        // Agregar estilos al botón
        tutorialButton.style.cssText = `
          margin-left: 8px;
          font-size: 0.875rem;
          padding: 0.375rem 0.75rem;
          border: 1px solid #17a2b8;
          background-color: transparent;
          color: #17a2b8;
          border-radius: 0.25rem;
          cursor: pointer;
          transition: all 0.15s ease-in-out;
        `;

        // Agregar hover effect
        tutorialButton.addEventListener("mouseenter", () => {
          tutorialButton.style.backgroundColor = "#17a2b8";
          tutorialButton.style.color = "white";
        });

        tutorialButton.addEventListener("mouseleave", () => {
          tutorialButton.style.backgroundColor = "transparent";
          tutorialButton.style.color = "#17a2b8";
        });

        // Agregar evento click
        tutorialButton.addEventListener("click", () => {
          vnode.context.$showTutorial(modelName);
        });

        // Agregar el botón al elemento
        el.appendChild(tutorialButton);

        // Guardar referencia al botón para poder removerlo después
        el._tutorialButton = tutorialButton;
      },

      unbind(el) {
        // Remover el botón cuando se desmonte el elemento
        if (el._tutorialButton && el._tutorialButton.parentNode) {
          el._tutorialButton.parentNode.removeChild(el._tutorialButton);
        }
      },
    });


    // Configuración por defecto
    Vue.prototype.$tutorialConfig = {
      buttonPosition: "bottom-right",
      showButtonText: true,
      autoShowTooltip: true,
      autoStart: false,
      ...options,
    };
  },
};

export default TutorialPlugin;
